import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import {
  Users,
  Calendar,
  FileText,
  TrendingUp,
  ArrowRight,
  ShieldAlert,
  KeyRound,
  UserCheck,
  Clock,
  PlugZap,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { JumpstartWeeklySummary } from '../../components/admin/JumpstartWeeklySummary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [security, setSecurity] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [bookingStats, setBookingStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, leadsRes, bookingsRes, securityRes, integrationsRes, bookingAnalyticsRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true }),
          axios.get(`${API_URL}/api/admin/leads?limit=5`, { withCredentials: true }),
          axios.get(`${API_URL}/api/admin/bookings?limit=5`, { withCredentials: true }),
          axios.get(`${API_URL}/api/admin/dashboard/security-summary`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API_URL}/api/admin/integrations-status`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API_URL}/api/admin/booking-analytics`, { withCredentials: true }).catch(() => ({ data: null })),
        ]);
        setStats(statsRes.data);
        setRecentLeads(leadsRes.data);
        setRecentBookings(bookingsRes.data);
        setSecurity(securityRes.data);
        setIntegrations(integrationsRes.data);
        setBookingStats(bookingAnalyticsRes.data);
      } catch (error) {
        console.error('Error fetching admin data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    { label: 'Total Leads', value: stats?.total_leads || 0, icon: Users, color: 'bg-[#EFF6FF] text-[#1D4ED8]', link: '/admin/leads' },
    { label: 'New Leads', value: stats?.new_leads || 0, icon: TrendingUp, color: 'bg-[#FFF7ED] text-[#F97316]', link: '/admin/leads' },
    { label: 'Total Bookings', value: stats?.total_bookings || 0, icon: Calendar, color: 'bg-[#EFF6FF] text-[#1D4ED8]', link: '/admin/bookings' },
    { label: 'Pending Bookings', value: stats?.pending_bookings || 0, icon: FileText, color: 'bg-[#FFF7ED] text-[#F97316]', link: '/admin/bookings' },
    { label: 'Total Clients', value: stats?.total_clients || 0, icon: Users, color: 'bg-green-100 text-green-700', link: '/admin/clients' },
    { label: 'Active Clients', value: stats?.active_clients || 0, icon: TrendingUp, color: 'bg-purple-100 text-purple-700', link: '/admin/clients' },
    { label: 'Programs', value: stats?.total_programs || 0, icon: FileText, color: 'bg-[#EFF6FF] text-[#1D4ED8]', link: '/admin/programs' },
    { label: 'Events', value: stats?.total_events || 0, icon: Calendar, color: 'bg-[#FFF7ED] text-[#F97316]', link: '/admin/events' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <AdminLayout title="Dashboard">
      <div data-testid="admin-dashboard">
        <h1 className="heading-2 text-2xl text-[#0A0F1C] mb-8" data-testid="admin-title">Dashboard</h1>

        <JumpstartWeeklySummary />

        {/* Security Snapshot */}
        {security && (
          <div className="bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#0F172A] text-white rounded-2xl p-5 mb-8" data-testid="admin-security-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ShieldAlert size={18} className="text-[#FB923C]" />
                <h2 className="text-sm font-bold uppercase tracking-wider">Security snapshot</h2>
              </div>
              <Link to="/admin/activity" className="text-xs text-[#94A3B8] hover:text-white flex items-center gap-1">
                View activity <ArrowRight size={12} />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white/5 border border-white/10 rounded-xl p-3" data-testid="security-stat-failed">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#94A3B8] mb-1.5"><KeyRound size={11} /> Failed logins 24h</div>
                <div className={`text-2xl font-bold ${security.failed_logins_24h > 0 ? 'text-amber-400' : 'text-white'}`}>{security.failed_logins_24h}</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-3" data-testid="security-stat-locked">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#94A3B8] mb-1.5"><KeyRound size={11} /> Locked accounts</div>
                <div className={`text-2xl font-bold ${security.locked_accounts > 0 ? 'text-red-400' : 'text-white'}`}>{security.locked_accounts}</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-3" data-testid="security-stat-idle">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#94A3B8] mb-1.5"><Clock size={11} /> Idle {security.idle_days}d+</div>
                <div className="text-2xl font-bold text-white">{security.idle_users}</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-3" data-testid="security-stat-admin-logins">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#94A3B8] mb-1.5"><UserCheck size={11} /> Admin logins 24h</div>
                <div className="text-2xl font-bold text-white">{security.admin_logins_24h}</div>
              </div>
            </div>
          </div>
        )}

        {/* Integrations Status */}
        {integrations && (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 mb-8" data-testid="admin-integrations-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <PlugZap size={18} className="text-[#1D4ED8]" />
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Integrations Status</h2>
              </div>
              <div className="text-xs text-[#64748B]">
                {Object.values(integrations).filter((v) => v?.configured).length}/{Object.keys(integrations).length} configured
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="integrations-grid">
              <TooltipProvider delayDuration={150}>
                {Object.entries(integrations).map(([name, info]) => {
                  const ok = !!info?.configured;
                  const missing = Array.isArray(info?.missing_keys) ? info.missing_keys : [];
                  const tooltipBody = ok
                    ? `${name} is configured and ready.`
                    : missing.length
                      ? `Missing env vars: ${missing.join(', ')}`
                      : `${name} not configured.`;
                  return (
                    <Tooltip key={name}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label={`${name.replace(/_/g, ' ')} - ${tooltipBody}`}
                          data-testid={`integration-${name}`}
                          className={`text-left w-full rounded-xl p-3 border focus:outline-none focus:ring-2 focus:ring-[#1D4ED8] ${ok ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold uppercase tracking-wider text-[#0A0F1C]">{name.replace(/_/g, ' ')}</span>
                            {ok ? (
                              <CheckCircle2 size={14} className="text-emerald-600" aria-hidden="true" />
                            ) : (
                              <AlertTriangle size={14} className="text-amber-600" aria-hidden="true" />
                            )}
                          </div>
                          <div className={`text-xs ${ok ? 'text-emerald-700' : 'text-amber-700'}`}>
                            {ok ? 'Configured' : (missing.length ? `Missing ${missing.length} key${missing.length > 1 ? 's' : ''}` : 'Not configured')}
                          </div>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p className="text-xs">{tooltipBody}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </TooltipProvider>
            </div>
          </div>
        )}

        {/* Bookings & Requests pipeline */}
        {bookingStats && (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 mb-8" data-testid="booking-analytics-card">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h2 className="font-bold text-[#0A0F1C] flex items-center gap-2"><Calendar size={16} className="text-[#1D4ED8]" /> Bookings &amp; Requests</h2>
              <div className="flex items-center gap-3 text-xs font-semibold">
                <Link to="/admin/calendar" className="text-[#1D4ED8] hover:underline" data-testid="ba-view-calendar">View calendar</Link>
                <Link to="/admin/time-requests" className="text-[#1D4ED8] hover:underline" data-testid="ba-view-requests">View requests</Link>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <BaStat label="Upcoming" value={bookingStats.upcoming_bookings} color="#1D4ED8" testid="ba-upcoming" />
              <BaStat label="This week" value={bookingStats.booked_this_week} color="#0EA5E9" testid="ba-week" />
              <BaStat label="Pending requests" value={bookingStats.requests?.pending || 0} color="#F97316" testid="ba-pending" highlight={(bookingStats.requests?.pending || 0) > 0} />
              <BaStat label="Approved (30d)" value={bookingStats.requests?.approved || 0} color="#059669" testid="ba-approved" />
              <BaStat label="Cancelled (30d)" value={bookingStats.cancelled_recent} color="#64748B" testid="ba-cancelled" />
              <BaStat label="No-shows (30d)" value={bookingStats.noshows_recent} color="#DC2626" testid="ba-noshows" />
            </div>
            {bookingStats.pending_requests?.length > 0 && (
              <div className="mt-4 border-t border-[#F1F5F9] pt-3">
                <div className="text-xs font-bold text-[#94A3B8] uppercase tracking-wide mb-2">Awaiting your approval</div>
                <div className="space-y-1.5">
                  {bookingStats.pending_requests.map((r) => (
                    <Link key={r.request_id} to="/admin/time-requests" className="flex items-center justify-between text-sm hover:bg-[#F8FAFC] rounded-lg px-2 py-1.5 -mx-2" data-testid={`ba-pending-${r.request_id}`}>
                      <span className="text-[#0A0F1C] font-medium truncate">{r.name} · {r.service_name}</span>
                      <span className="text-[#F97316] font-semibold shrink-0 ml-2 flex items-center gap-1"><Clock size={12} /> {r.preferred_date} {r.preferred_time}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Link
                to={stat.link}
                key={index}
                className="bg-white p-6 border border-[#E2E8F0] rounded-lg hover:shadow-md transition-shadow"
                data-testid={`stat-card-${index}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${stat.color.split(' ')[0]}`}>
                    <Icon size={24} className={stat.color.split(' ')[1]} />
                  </div>
                </div>
                <div className="text-3xl font-bold text-[#0A0F1C] mb-1">{stat.value}</div>
                <div className="text-sm text-[#475569]">{stat.label}</div>
              </Link>
            );
          })}
        </div>

        {/* Recent Activity */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Recent Leads */}
          <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-bold text-[#0A0F1C]">Recent Leads</h2>
              <Link 
                to="/admin/leads" 
                className="text-sm text-[#1D4ED8] font-medium hover:underline flex items-center gap-1"
                data-testid="view-all-leads-link"
              >
                View All <ArrowRight size={14} />
              </Link>
            </div>
            
            {recentLeads.length > 0 ? (
              <div className="space-y-4">
                {recentLeads.map((lead, index) => (
                  <div 
                    key={lead.lead_id || index}
                    className="flex items-center justify-between py-3 border-b border-[#F1F5F9] last:border-0"
                    data-testid={`recent-lead-${index}`}
                  >
                    <div>
                      <div className="font-medium text-[#0A0F1C]">{lead.name}</div>
                      <div className="text-sm text-[#475569]">{lead.email}</div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      lead.status === 'new' 
                        ? 'bg-[#FFF7ED] text-[#F97316]' 
                        : 'bg-[#F1F5F9] text-[#475569]'
                    }`}>
                      {lead.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#475569] text-sm">No leads yet</p>
            )}
          </div>

          {/* Recent Bookings */}
          <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-bold text-[#0A0F1C]">Recent Bookings</h2>
              <Link 
                to="/admin/bookings" 
                className="text-sm text-[#1D4ED8] font-medium hover:underline flex items-center gap-1"
                data-testid="view-all-bookings-link"
              >
                View All <ArrowRight size={14} />
              </Link>
            </div>
            
            {recentBookings.length > 0 ? (
              <div className="space-y-4">
                {recentBookings.map((booking, index) => (
                  <div 
                    key={booking.booking_id || index}
                    className="flex items-center justify-between py-3 border-b border-[#F1F5F9] last:border-0"
                    data-testid={`recent-booking-${index}`}
                  >
                    <div>
                      <div className="font-medium text-[#0A0F1C]">{booking.name}</div>
                      <div className="text-sm text-[#475569]">{booking.service_type}</div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      booking.status === 'pending' 
                        ? 'bg-[#FFF7ED] text-[#F97316]' 
                        : booking.status === 'confirmed'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-[#F1F5F9] text-[#475569]'
                    }`}>
                      {booking.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#475569] text-sm">No bookings yet</p>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}


function BaStat({ label, value, color, testid, highlight }) {
  return (
    <div className={`rounded-xl p-3 border ${highlight ? 'border-[#F97316] bg-[#FFF7ED]' : 'border-[#E2E8F0] bg-[#F8FAFC]'}`} data-testid={testid}>
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
      <div className="text-[11px] text-[#475569] leading-tight mt-0.5">{label}</div>
    </div>
  );
}
